'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createVoiceSocket } from './socket';
import { elowenClient } from '../../lib/elowenClient';
import { VOICE_DICTATION_PAUSE_DEFAULT_SECONDS } from '../../lib/voice';
import type { UserVoiceSettingsSnapshot } from '../../lib/types';
import { createVoiceCapture } from 'elowen-plugin-shared/voiceAudio';
import { DictationSession, type DictationFailure, type DictationState } from './dictationSession';
import { DictationDraft } from './dictationDraft';
import { voicePreferencesKey, voicePreferencesQuery } from './voicePreferences';

/** The draft a dictation writes into: the composer's textarea and the setter that owns its value. */
export interface DictationTarget {
  read(): { value: string; start: number; end: number } | null;
  write(value: string, caret: number): void;
}

/** Live dictation into a draft. `start` listens and the text appears while the person speaks. `stop` and
 *  `finish` end it and keep the text (`finish` resolves once the final text is in, so a send can follow),
 *  `cancel` ends it and removes only what this dictation inserted. The microphone is released on every exit. */
export function useDictation(target: DictationTarget, onFailure: (failure: DictationFailure) => void) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<DictationState>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const draft = useRef<DictationDraft | null>(null);
  const handlers = useRef({ target, onFailure });
  handlers.current = { target, onFailure };

  const apply = useCallback((change: (draft: DictationDraft, value: string, caret: number) => { value: string; caret: number }): void => {
    const current = handlers.current.target.read();
    if (!current || !draft.current) return;
    const next = change(draft.current, current.value, current.end);
    handlers.current.target.write(next.value, next.caret);
  }, []);

  const session = useRef<DictationSession | null>(null);
  if (!session.current) {
    session.current = new DictationSession({
      capture: createVoiceCapture,
      start: elowenClient.startVoiceDictation,
      socket: createVoiceSocket,
      // Until the account's preference has loaded, the default pause applies.
      pauseSeconds: () => {
        const saved = queryClient.getQueryData<UserVoiceSettingsSnapshot>(voicePreferencesKey);
        return saved ? saved.dictationPauseSeconds : VOICE_DICTATION_PAUSE_DEFAULT_SECONDS;
      },
    }, {
      state: setState,
      delta: (itemId, text) => { apply((d, value, caret) => d.delta(itemId, text, value, caret)); },
      final: (itemId, text) => { apply((d, value, caret) => d.final(itemId, text, value, caret)); },
      ended: (failure) => { if (failure) handlers.current.onFailure(failure); },
    });
  }
  const live = session.current;

  // The clock runs while the microphone is open.
  useEffect(() => {
    if (state !== 'listening') { setElapsedMs(0); return; }
    const startedAt = Date.now();
    const timer = setInterval(() => { setElapsedMs(Date.now() - startedAt); }, 250);
    return () => { clearInterval(timer); };
  }, [state]);

  const start = useCallback(async (): Promise<void> => {
    const current = handlers.current.target.read();
    if (!current || live.state !== 'idle') return;
    draft.current = new DictationDraft(current.end);
    draft.current.edit(current.value, current.end);
    // The pause that stops a quiet dictation is read while it listens; load it without delaying the microphone.
    void queryClient.prefetchQuery(voicePreferencesQuery);
    await live.start();
  }, [live, queryClient]);
  const beforeEdit = useCallback((start: number, end: number): void => { draft.current?.beforeEdit(start, end); }, []);
  const edit = useCallback((value: string, caret: number): void => { draft.current?.edit(value, caret); }, []);
  const finish = useCallback((): Promise<void> => live.finish(), [live]);
  const stop = useCallback((): void => { void live.finish(); }, [live]);
  const cancel = useCallback((): void => {
    const active = live.state !== 'idle';
    live.cancel();
    if (active) apply((d, value, caret) => d.withdraw(value, caret));
  }, [live, apply]);

  // Leaving the surface mid-dictation frees the microphone; the text already in the draft stays there.
  useEffect(() => () => { live.dispose(); }, [live]);

  return { state, elapsedMs, start, finish, stop, cancel, beforeEdit, edit };
}
